import React, { useEffect, useMemo, useState } from "react";
import {
    completePasswordSetup,
    getPasswordTokenInfo,
} from "./api";
import logoLight from "./assets/logo-light.svg";
import logoDark from "./assets/logo-dark.svg";

function readTokenFromHash() {
    if (typeof window === "undefined") return "";
    const hash = window.location.hash || "";
    const queryIndex = hash.indexOf("?");
    const query = queryIndex >= 0 ? hash.slice(queryIndex + 1) : "";
    const params = new URLSearchParams(query);
    return String(params.get("token") || "").trim();
}

function SetPasswordPage() {
    const token = useMemo(() => readTokenFromHash(), []);
    const [loading, setLoading] = useState(true);
    const [ready, setReady] = useState(false);
    const [purpose, setPurpose] = useState("setup");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [isDark, setIsDark] = useState(false);

    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;

        const mq = window.matchMedia("(prefers-color-scheme: dark)");
        const updateMode = () => setIsDark(mq.matches);
        updateMode();

        if (mq.addEventListener) {
            mq.addEventListener("change", updateMode);
        } else if (mq.addListener) {
            mq.addListener(updateMode);
        }

        return () => {
            if (mq.removeEventListener) {
                mq.removeEventListener("change", updateMode);
            } else if (mq.removeListener) {
                mq.removeListener(updateMode);
            }
        };
    }, []);

    useEffect(() => {
        if (typeof document === "undefined") return;
        const previousBg = document.body.style.backgroundColor;
        document.body.style.backgroundColor = isDark ? "#020617" : "#f3f4f6";
        return () => {
            document.body.style.backgroundColor = previousBg;
        };
    }, [isDark]);

    useEffect(() => {
        let cancelled = false;

        async function load() {
            if (!token) {
                setError("Missing password link token.");
                setLoading(false);
                return;
            }

            setLoading(true);
            setError("");
            const data = await getPasswordTokenInfo(token);
            if (cancelled) return;

            if (!data?.ok) {
                setError(data?.message || "Invalid or expired password link.");
                setReady(false);
                setLoading(false);
                return;
            }

            setPurpose(data.purpose === "reset" ? "reset" : "setup");
            setUsername(data.username || "");
            setReady(true);
            setLoading(false);
        }

        load();
        return () => {
            cancelled = true;
        };
    }, [token]);

    const heading = purpose === "reset" ? "Reset Password" : "Set Password";
    const themedStyles = getStyles(isDark);
    const passwordRequirementText =
        "Use at least 8 characters, including 1 number and 1 special character.";
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (saving) return;

        setError("");

        if (!password || password.length < 8) {
            setError(passwordRequirementText);
            return;
        }

        if (!/\d/.test(password)) {
            setError(passwordRequirementText);
            return;
        }

        if (!/[^A-Za-z0-9]/.test(password)) {
            setError(passwordRequirementText);
            return;
        }

        if (password !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        setSaving(true);
        const data = await completePasswordSetup(token, password);
        setSaving(false);

        if (!data?.ok) {
            setError(data?.message || "Failed to set password.");
            return;
        }
        if (typeof window !== "undefined") {
            const loginMessage =
                purpose === "reset"
                    ? "Password reset. You can now sign in"
                    : "Password set. You can now sign in";
            window.location.hash = `#/dashboard?auth_message=${encodeURIComponent(loginMessage)}`;
        }
    };

    return (
        <div style={themedStyles.page}>
            <div style={themedStyles.card}>
                <div style={themedStyles.headerBox}>
                    <img
                        src={isDark ? logoLight : logoDark}
                        alt="Urban Podcasts"
                        style={themedStyles.logoImage}
                    />
                </div>
                <h2 style={themedStyles.title}>{heading}</h2>
                {username ? (
                    <p style={themedStyles.subtitle}>
                        Account: <strong>{username}</strong>
                    </p>
                ) : null}

                {loading ? <p style={themedStyles.info}>Validating link…</p> : null}
                {error ? <div style={themedStyles.error}>{error}</div> : null}
                {!loading && ready && (
                    <form onSubmit={handleSubmit} style={themedStyles.form}>
                        <label style={themedStyles.label}>
                            New password
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                style={themedStyles.input}
                            />
                        </label>
                        <p style={themedStyles.requirements}>
                            {passwordRequirementText}
                        </p>
                        <label style={themedStyles.label}>
                            Confirm password
                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                style={themedStyles.input}
                            />
                        </label>
                        <button type="submit" disabled={saving} style={themedStyles.button}>
                            {saving ? "Saving…" : "Save password"}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}

function getStyles(isDark) {
    return {
        page: {
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: isDark ? "#020617" : "#f3f4f6",
            padding: "24px",
        },
        card: {
            width: "100%",
            maxWidth: "460px",
            borderRadius: "14px",
            border: isDark ? "1px solid rgba(148,163,184,0.3)" : "1px solid #e5e7eb",
            background: isDark ? "rgba(2,6,23,0.92)" : "#ffffff",
            padding: "24px",
            color: isDark ? "#e5e7eb" : "#111827",
            boxShadow: isDark
                ? "0 20px 60px rgba(0,0,0,0.55)"
                : "0 20px 60px rgba(15,23,42,0.15)",
        },
        title: {
            margin: "0 0 6px 0",
            fontSize: "24px",
            color: isDark ? "#f9fafb" : "#111827",
        },
        headerBox: {
            textAlign: "center",
            marginBottom: 8,
        },
        logoImage: {
            width: 220,
            maxWidth: "100%",
            height: "auto",
            margin: "0 auto 8px",
            display: "block",
        },
        subtitle: {
            margin: "0 0 16px 0",
            color: isDark ? "#9ca3af" : "#6b7280",
        },
        form: {
            display: "grid",
            gap: "12px",
            marginTop: "10px",
        },
        requirements: {
            margin: 0,
            fontSize: "12px",
            color: isDark ? "#94a3b8" : "#6b7280",
        },
        label: {
            display: "grid",
            gap: "6px",
            fontSize: "14px",
            color: isDark ? "#e5e7eb" : "#374151",
        },
        input: {
            borderRadius: "10px",
            border: isDark ? "1px solid #334155" : "1px solid #d1d5db",
            background: isDark ? "#020617" : "#ffffff",
            color: isDark ? "#e5e7eb" : "#111827",
            padding: "10px 12px",
            fontSize: "14px",
        },
        button: {
            marginTop: "6px",
            border: "none",
            borderRadius: "999px",
            background: isDark ? "#2563eb" : "#1d3557",
            color: "#fff",
            padding: "10px 16px",
            cursor: "pointer",
        },
        error: {
            marginTop: "8px",
            borderRadius: "10px",
            background: isDark ? "#7f1d1d" : "#fee2e2",
            border: isDark ? "1px solid #fca5a5" : "1px solid #fecaca",
            color: isDark ? "#fecaca" : "#b91c1c",
            padding: "10px 12px",
        },
        success: {
            marginTop: "8px",
            borderRadius: "10px",
            background: isDark ? "#064e3b" : "#dcfce7",
            border: isDark ? "1px solid #6ee7b7" : "1px solid #86efac",
            color: isDark ? "#d1fae5" : "#166534",
            padding: "10px 12px",
        },
        info: {
            color: isDark ? "#9ca3af" : "#6b7280",
            marginTop: "8px",
        },
    };
}

export default SetPasswordPage;
